import React, { useEffect } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GraduationCapIcon, BookOpenIcon, HeartIcon, UsersIcon, ShieldCheckIcon, ClockIcon, DollarSignIcon, AwardIcon, CheckCircleIcon, ArrowRightIcon, StarIcon, QuoteIcon, CheckIcon } from 'lucide-react';
import { getFindDegreesTemplate } from '../data/findDegreesTemplates';
import { getProgramTemplate, programsIndexSlug } from '../data/programTemplates';
import { ProgramTemplatePage } from './ProgramTemplatePage';
import { ProgramDomainsPage } from './ProgramDomainsPage';
import { usePageMeta } from '../hooks/usePageMeta';
const benefitIconMap: Record<string, React.ElementType> = {
  GraduationCapIcon,
  BookOpenIcon,
  HeartIcon,
  UsersIcon,
  ShieldCheckIcon,
  ClockIcon,
  DollarSignIcon,
  AwardIcon
};
/**
 * Resolves a slug to the right template: the program index, a domain page, an
 * individual program page, or the original lead-gen landing template. Explicit
 * routes are declared in App.tsx (so each URL shows up on its own), which means
 * the slug is read from the path when there is no `:template` param to read.
 *
 * Program content lives under `/program-information/`; anything reaching the
 * legacy `/find-degrees/` path with a program slug is redirected there so old
 * links keep working.
 */
export function FindDegreesPage() {
  const {
    template
  } = useParams<{
    template: string;
  }>();
  const location = useLocation();
  const pathSlug = location.pathname.split('/').filter(Boolean).pop() || '';
  const slug = (template || pathSlug).toLowerCase();
  const isLegacyPath = location.pathname.startsWith('/find-degrees/');
  const program = getProgramTemplate(slug);
  const isProgramContent = slug === programsIndexSlug || !!program;
  if (isLegacyPath && isProgramContent) {
    return <Navigate to={`/program-information/${slug}${location.search}`} replace data-id="element-1472" data-mp="2r9275fnm9d82" />;
  }
  if (slug === programsIndexSlug) {
    return <ProgramDomainsPage data-id="element-1473" data-mp="2ho2lt2bvvttz" />;
  }
  if (program) {
    return <ProgramTemplatePage key={program.slug} program={program} data-id="element-1474" data-mp="2mlualnh8ezty" />;
  }
  return <FindDegreesLandingTemplate data-id="element-1475" data-mp="28lb9l0b6ou1a" />;
}
function FindDegreesLandingTemplate() {
  const {
    template
  } = useParams<{
    template: string;
  }>();
  const navigate = useNavigate();
  const data = template ? getFindDegreesTemplate(template) : undefined;
  usePageMeta(data?.metaTitle || 'Find Your Degree | OnlineCollegeGuide', data?.metaDescription || 'Find accredited online degree programs that fit your goals.', {
    ogType: 'website'
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [template]);
  if (!data) {
    return <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4" data-id="element-1476" data-mp="2vjo8v3yk43jr">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-1477" data-mp="3j9s9d2gg7n6j">Page Not Found</h1>
        <p className="text-gray-600 mb-8" data-id="element-1478" data-mp="3o2levsjqqzjo">
          We couldn't find the page you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-1479" data-mp="1cl8e7vi8ac97">
          Return Home
        </Link>
      </div>;
  }
  const accent = data.accentColor;
  const accentDark = data.accentColorDark;
  const handleCta = () => {
    navigate(data.ctaHref);
  };
  return <main className="min-h-screen bg-white" data-id="element-1480" data-mp="tnywqjwgr8ul">
      {/* Minimal Top Bar */}
      <div className="bg-white border-b border-gray-100" data-id="element-1481" data-mp="tq1igrql2fti">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between" data-id="element-1482" data-mp="opxd9xv4l7il">
          <Link to="/" className="flex items-center" data-id="element-1483" data-mp="13n69p5zad1h6">
            <img src="https://cdn.magicpatterns.com/uploads/hM6pwxGTxH7R3Ug6xJ6sm8/Horizontal_OCG_logo.png" alt="Online College Guide - Your Path Forward" className="h-10 w-auto object-contain" data-id="element-1484" data-mp="yn1qh86ztsn9" />
          </Link>
          <div className="hidden sm:flex items-center text-xs font-semibold text-gray-500 tracking-wider uppercase" data-id="element-1485" data-mp="2a1xehkrj56gh">
            <ShieldCheckIcon className="w-4 h-4 mr-1.5 text-green-600" data-id="element-1486" data-mp="jnfz5hugbvfy" />
            Trusted by 2M+ Students
          </div>
        </div>
      </div>

      {/* Hero - light theme matching reference layout */}
      <section className="bg-gray-50 relative overflow-hidden" data-id="element-1487" data-mp="xkut8zvngre9">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20" data-id="element-1488" data-mp="xmfneoe1y8bl">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center" data-id="element-1489" data-mp="12iw8f9pyg6kg">
            {/* Left: Copy */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-1490" data-mp="17hr1r5ljtdit">
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-widest text-white mb-6" style={{
              backgroundColor: '#14479E'
            }} data-id="element-1491" data-mp="1lc08bctrnfni">
                <span className="w-1.5 h-1.5 rounded-full" style={{
                backgroundColor: '#8FBEF5'
              }} data-id="element-1492" data-mp="1vdca8h3zamng" />
                {data.heroEyebrowBadge || data.eyebrow}
              </span>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-gray-900 mb-6 tracking-tight" data-id="element-1493" data-mp="2j3osaksxtzrr">
                {data.heroHeadlinePrefix && <>{data.heroHeadlinePrefix} </>}
                {data.heroHeadlineHighlight && <span style={{
                color: '#14479E'
              }} data-id="element-1494" data-mp="29clnp90sv2nq">
                    {data.heroHeadlineHighlight}
                  </span>}
                {data.heroHeadlineSuffix && <>
                    <br data-id="element-1495" data-mp="2x2687wclq7z4" />
                    {data.heroHeadlineSuffix}
                  </>}
                {!data.heroHeadlinePrefix && !data.heroHeadlineHighlight && !data.heroHeadlineSuffix && data.headline}
              </h1>

              <p className="text-base sm:text-lg text-gray-600 max-w-xl leading-relaxed" data-id="element-1496" data-mp="lg44j04a0jub">
                {data.subheadline}
              </p>
            </motion.div>

            {/* Right: Step indicator + Card */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} data-id="element-1497" data-mp="chseaz7bno2e">
              {/* Step Indicator */}
              <div className="flex items-center justify-between mb-5 px-2 sm:px-4" data-id="element-1498" data-mp="1ww2hvg6hls1c">
                <div className="flex items-center gap-2.5" data-id="element-1499" data-mp="21tchfonlbk2w">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#1668E0'
                }} data-id="element-1500" data-mp="3mcevsqyrt5uc">
                    <CheckIcon className="w-4 h-4 text-white" strokeWidth={3} data-id="element-1501" data-mp="3w3j97m8ssin2" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900" data-id="element-1502" data-mp="3raq3ow5i969x">
                    {data.heroStep1Label || 'Profile Complete'}
                  </span>
                </div>
                <div className="flex-1 h-px bg-gray-300 mx-3 sm:mx-4" data-id="element-1503" data-mp="4wyd3o3vykj6" />
                <div className="flex items-center gap-2.5" data-id="element-1504" data-mp="ivxz0o00klbn">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#14479E'
                }} data-id="element-1505" data-mp="ivfz9he57oyz">
                    <span className="text-xs font-bold text-white" data-id="element-1506" data-mp="2fo7yhri3akz7">2</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-500" data-id="element-1507" data-mp="1tv2al70c0av0">
                    {data.heroStep2Label || 'Confirm Email'}
                  </span>
                </div>
              </div>

              {/* Card */}
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 sm:p-10 text-center" data-id="element-1508" data-mp="23kc5svqh29nc">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2" data-id="element-1509" data-mp="28ix42pm5yj95">
                  {data.heroCardHeading || data.headline}
                </h2>
                {data.heroCardSubtext && <p className="text-sm font-semibold mb-6" style={{
                color: '#14479E'
              }} data-id="element-1510" data-mp="1fw2ljy8wptyb">
                    {data.heroCardSubtext}
                  </p>}
                <button type="button" onClick={handleCta} className="group inline-flex items-center justify-center text-white font-bold py-3.5 px-8 rounded-lg text-base transition-all duration-200 shadow-md hover:shadow-lg" style={{
                backgroundColor: '#FF9F04'
              }} onMouseEnter={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#E08C00';
              }} onMouseLeave={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#FF9F04';
              }} data-id="element-1511" data-mp="2wa2m9bfktnmm">
                  {data.ctaLabel}
                  <ArrowRightIcon className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" data-id="element-1512" data-mp="3t2si68xvxu98" />
                </button>
                {data.heroCardHelper && <p className="mt-6 text-xs sm:text-sm text-gray-500 leading-relaxed max-w-xs mx-auto" data-id="element-1513" data-mp="1raw710fmqmez">
                    {data.heroCardHelper}
                  </p>}
              </div>

              {/* Trust strip below card */}
              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-gray-500" data-id="element-1514" data-mp="2x6cyiymjjdyw">
                {data.trustItems.map(item => <div key={item} className="flex items-center" data-id="element-1515" data-mp="363ne2w6e5p9m">
                    <CheckCircleIcon className="w-3.5 h-3.5 text-green-600 mr-1.5 flex-shrink-0" data-id="element-1516" data-mp="322tozzh7q4f5" />
                    {item}
                  </div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 lg:py-20 bg-white" data-id="element-1517" data-mp="2n62lbe0ia6jq">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1518" data-mp="2n6vv6nurhc63">
          <div className="text-center max-w-2xl mx-auto mb-12" data-id="element-1519" data-mp="2ieucok702p7m">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{
            color: accent
          }} data-id="element-1520" data-mp="2id9k3048xgce">
              {data.benefitsHeading}
            </h2>
            <div className="w-16 h-1 mx-auto rounded-full" style={{
            backgroundColor: '#1668E0'
          }} data-id="element-1521" data-mp="1gk9hi6kmbb9g" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1522" data-mp="1lcsvh4n7w8es">
            {data.benefits.map((benefit, i) => {
            const Icon = benefitIconMap[benefit.icon] || BookOpenIcon;
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow" data-id="element-1523" data-mp="1za7pkmof14d3" data-mpi={i}>
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{
                backgroundColor: `${accent}15`
              }} data-id="element-1524" data-mp="2ljfvyfcqq8g1">
                    <Icon className="w-6 h-6" style={{
                  color: accent
                }} data-id="element-1525" data-mp="2bra1myz0nexo" />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{
                color: accent
              }} data-id="element-1526" data-mp="21su9qts601n4">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1527" data-mp="1nw7f4xnopnkd">
                    {benefit.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Featured Schools */}
      <section className="py-14 bg-gray-50 border-y border-gray-200" data-id="element-1528" data-mp="3psxzlefesqnk">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1529" data-mp="3psobxxhu6vl1">
          <h2 className="text-center text-sm font-bold uppercase tracking-widest mb-8 text-gray-500" data-id="element-1530" data-mp="3uqzju2okmw0m">
            {data.schoolsHeading}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-1531" data-mp="3fprppb9kxbw">
            {data.schoolNames.map(name => <div key={name} className="bg-white border border-gray-200 rounded-lg p-4 text-center flex items-center justify-center min-h-[80px]" data-id="element-1532" data-mp="36ulohn4xt6pc">
                <span className="text-sm font-semibold leading-tight" style={{
              color: accent
            }} data-id="element-1533" data-mp="3grqgw4pdlyel">
                  {name}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      {data.testimonial && <section className="py-16 lg:py-20 bg-white" data-id="element-1534" data-mp="112ykm5dlh8nx">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1535" data-mp="yn8hg4y7srkp">
            <QuoteIcon className="w-12 h-12 mx-auto mb-6 opacity-30" style={{
          color: accent
        }} data-id="element-1536" data-mp="uln9my0jf3hd" />
            <div className="flex items-center justify-center gap-1 mb-4" data-id="element-1537" data-mp="13ljrafoif22x">
              {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className="w-5 h-5 fill-current" style={{
            color: '#5FA0EE'
          }} data-id="element-1538" data-mp="1hkt5xax2ho4s" />)}
            </div>
            <p className="text-xl sm:text-2xl font-medium leading-relaxed mb-6 italic" style={{
          color: accent
        }} data-id="element-1539" data-mp="1ramjuikomfc7">
              "{data.testimonial.quote}"
            </p>
            <p className="font-bold text-gray-900" data-id="element-1540" data-mp="1wa7eigstk1j4">{data.testimonial.name}</p>
            <p className="text-sm text-gray-500" data-id="element-1541" data-mp="1w84k7kkmvm8l">{data.testimonial.detail}</p>
          </div>
        </section>}

      {/* Bottom CTA */}
      <section className="py-16 lg:py-20" style={{
      backgroundColor: accentDark
    }} data-id="element-1542" data-mp="2jxvzt9xdmc8x">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1543" data-mp="25x1rerabwba9">
          <h2 className="text-2xl sm:text-4xl font-extrabold text-white mb-4 leading-tight" data-id="element-1544" data-mp="2aw4ui66r07fl">
            {data.bottomCtaHeading}
          </h2>
          <p className="text-base sm:text-lg text-gray-300 mb-8 leading-relaxed" data-id="element-1545" data-mp="1w5x8xhlg28ao">
            {data.bottomCtaBody}
          </p>
          <button type="button" onClick={handleCta} className="group inline-flex items-center justify-center text-white font-bold py-4 px-10 rounded-lg text-base tracking-wide transition-all duration-200 shadow-lg hover:shadow-xl" style={{
          backgroundColor: '#FF9F04'
        }} onMouseEnter={e => {
          ;
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#E08C00';
        }} onMouseLeave={e => {
          ;
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#FF9F04';
        }} data-id="element-1546" data-mp="1n7lltpjsdsmz">
            {data.bottomCtaLabel}
            <ArrowRightIcon className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" data-id="element-1547" data-mp="kezl89f96mjc" />
          </button>
          <p className="mt-5 text-xs text-gray-400 uppercase tracking-widest" data-id="element-1548" data-mp="1i2rmpflcg9wq">
            Complementary • No Obligation • Takes 2 Minutes
          </p>
        </div>
      </section>

      {/* Footer mini */}
      <footer className="bg-gray-900 text-gray-400 text-xs py-6" data-id="element-1549" data-mp="183aheza07y1a">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-2" data-id="element-1550" data-mp="135hc08giz822">
          <p data-id="element-1551" data-mp="3sujbr0ojhqup">
            &copy; {new Date().getFullYear()} OnlineCollegeGuide. All rights
            reserved.
          </p>
          <p className="max-w-3xl mx-auto leading-relaxed" data-id="element-1552" data-mp="3nuy9aga5v0d8">
            Advertiser Disclosure: OnlineCollegeGuide.com is an
            advertising-supported site. Featured schools compensate us, which
            does not influence our editorial content.
          </p>
          <div className="flex justify-center gap-4 pt-2" data-id="element-1553" data-mp="28a5wq7f7ndgd">
            <Link to="/privacy-policy" className="hover:text-white transition-colors" data-id="element-1554" data-mp="2da0k40zs1kwo">
              Privacy Policy
            </Link>
            <Link to="/terms-and-conditions" className="hover:text-white transition-colors" data-id="element-1555" data-mp="2n269d2yn9o86">
              Terms of Service
            </Link>
          </div>
        </div>
      </footer>
    </main>;
}